import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** CSS width (number = px). */
  width?: number | string;
  align?: "left" | "right" | "center";
  /** Extra classes on the header and every cell, e.g. `hidden sm:table-cell` to drop a column on narrow screens. */
  className?: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** Makes rows focusable; click / Enter / Space call it. */
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  /** Number of skeleton rows while loading (default 5). */
  skeletonRows?: number;
  /** Rendered when not loading and `rows` is empty. */
  empty?: ReactNode;
  /** Accessible name of the table. */
  label?: string;
  className?: string;
}

const ALIGN = { left: "text-left", right: "text-right", center: "text-center" } as const;

/**
 * Data table: sticky uppercase 11.5 column labels, horizontal scroll container,
 * clickable + keyboard-operable rows, skeleton rows while loading, an empty slot.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  isLoading = false,
  skeletonRows = 5,
  empty,
  label,
  className,
}: DataTableProps<T>) {
  const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    // Only the focused row itself: Enter/Space on a button or link inside a cell is that control's own.
    if (event.target !== event.currentTarget) return;
    if (!onRowClick || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    onRowClick(row);
  };

  const showEmpty = !isLoading && rows.length === 0 && empty !== undefined;

  return (
    <div className={cn("overflow-x-auto font-ds", className)}>
      <table aria-label={label} aria-busy={isLoading || undefined} className="w-full border-collapse text-[13.5px]">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                style={column.width !== undefined ? { width: column.width } : undefined}
                className={cn(
                  "sticky top-0 z-[1] whitespace-nowrap border-b border-ds-line bg-ds-panel px-3 py-[9px] text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ds-ink3",
                  ALIGN[column.align ?? "left"],
                  column.className
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: skeletonRows }, (_, index) => (
              <tr key={`skeleton-${index}`} data-skeleton-row="">
                {columns.map((column) => (
                  <td key={column.id} className="border-b border-ds-line px-3 py-2.5">
                    <span className="block h-3.5 w-3/4 animate-pulse rounded bg-ds-sunk" />
                  </td>
                ))}
              </tr>
            ))}
          {!isLoading &&
            rows.map((row) => (
              <tr
                key={getRowId(row)}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (event) => onKeyDown(event, row) : undefined}
                className={cn(
                  "group [&:last-child>td]:border-b-0",
                  onRowClick && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-accent"
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      "border-b border-ds-line px-3 py-2.5 align-middle text-ds-ink2",
                      onRowClick && "group-hover:bg-ds-sunk",
                      ALIGN[column.align ?? "left"],
                      column.className
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          {showEmpty && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
